import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import classnames from "classnames";
import api from "../../api";
import InboxSidebar from "./Sidebar"; // or "./Sidebar" if you renamed it
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { messageState } from "../../Recoil/atom";
import { useRecoilState } from "recoil";
import { useNavigate } from "react-router-dom";

const Inbox = () => {
  document.title = "Inbox  | Pokémon Radiance";

  // "1" = Primary (Received), "3" = Sent (mirroring your NavLink logic)
  const [customActiveTab, setCustomActiveTab] = useState("1");
  const [messages, setMessages] = useRecoilState(messageState);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  // For selecting messages
  const [selectedIds, setSelectedIds] = useState([]);

  // For delete-all confirmation
  const [deleteAllModal, setDeleteAllModal] = useState(false);

  // Switch between "Primary" and "Sent"
  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setCustomActiveTab(tab);
    }
  };

  // ---------------------------
  // Fetch messages from backend
  // ---------------------------
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        let endpoint =
          customActiveTab === "1"
            ? "/v1/messages/received"
            : customActiveTab === "3"
              ? "/v1/messages/sent"
              : "";

        if (!endpoint) return;
        const response = await api.get(`${endpoint}`, {
          withCredentials: true,
        });

        const messagesData =
          response.data.received_messages || response.data.sent_messages || [];
        setMessages(messagesData);

        // Clear selection if user switches tabs
        setSelectedIds([]);
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    };

    fetchMessages();
  }, [customActiveTab, setMessages]);

  // ---------------------------------
  // Handle checkboxes for selected IDs
  // ---------------------------------
  const handleCheckboxChange = (messageId) => {
    setSelectedIds((prev) =>
      prev.includes(messageId)
        ? prev.filter((id) => id !== messageId)
        : [...prev, messageId],
    );
  };

  // -----------------------------------------------------
  // Delete selected messages (calls parent's handleDelete)
  // -----------------------------------------------------
  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return; // no selection
    try {
      // Perform multiple DELETE requests in parallel
      const deleteRequests = selectedIds.map((id) =>
        api.delete(`/v1/messages/delete/${id}`, {
          withCredentials: true,
        }),
      );
      await Promise.all(deleteRequests);

      // Remove from local state
      setMessages((prev) =>
        prev.filter((msg) => !selectedIds.includes(msg.id)),
      );
      setSelectedIds([]);
    } catch (err) {
      console.error("Error deleting selected messages:", err);
    }
  };

  // -------------
  // Delete All UI
  // -------------
  const openDeleteAllModal = () => setDeleteAllModal(true);
  const closeDeleteAllModal = () => setDeleteAllModal(false);

  // -------------
  // Delete All
  // -------------
  const handleDeleteAll = async () => {
    try {
      // Calls your "delete all received" endpoint
      await api.delete("/v1/messages/delete/received", {
        withCredentials: true,
      });

      // Clear out the messages in local state
      setMessages([]);
    } catch (err) {
      console.error("Error deleting all received messages:", err);
    } finally {
      closeDeleteAllModal();
    }
  };

  const handleReportMessage = async (messageId) => {
    try {
      await api.post(
        `/v1/messages/report/${messageId}`,
        {},
        {
          withCredentials: true, // or whatever your config is
        },
      );

      // Update local messages array
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, report: 1 } : msg)),
      );

      // Also update the selected message itself (so the modal sees the change)
      setSelectedMessage((prev) => (prev ? { ...prev, report: 1 } : null));

      // Let user know, including staff message
      alert(
        `Message has been reported! A staff member will view the message soon.`,
      );

      // Close the modal
      setModalOpen(false);
    } catch (err) {
      console.error("Error reporting message:", err);
      alert("Failed to report message.");
    }
  };

  // ------------------------
  // Viewing a message => mark as viewed
  // ------------------------
  const handleViewMessage = async (message) => {
    try {
      // Mark the message as viewed in the backend
      await api.patch(`/v1/messages/${message.id}/view`, null, {
        withCredentials: true,
      });

      // Update local state so that it’s no longer “unviewed”
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, view: 1 } : m)),
      );
    } catch (err) {
      console.error("Error marking message as viewed:", err);
    }

    console.log(message);

    // Open the modal
    setSelectedMessage(message);
    setModalOpen(true);
  };

  // -------------------
  // Close the modal
  // -------------------
  const toggleModal = () => setModalOpen(!modalOpen);

  // ----------------------
  // Reply button
  // ----------------------
  const handleReply = (username) => {
    navigate(`/send-message?to=${username}`);
  };

  const modalBodyStyle = {
    maxHeight: "400px",
    overflowY: "auto",
    wordWrap: "break-word",
    padding: "20px",
  };
  const dateStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "150px", // Adjust based on your layout
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Messages" breadcrumbItem="Inbox" />
          <Row>
            {/* SIDEBAR, with delete controls */}
            <InboxSidebar
              isPrimaryTabActive={customActiveTab === "1"}
              selectedIds={selectedIds}
              handleDeleteSelected={handleDeleteSelected}
              openDeleteAllModal={openDeleteAllModal}
            />

            {/* MAIN CONTENT */}
            <Col xl={9}>
              <Card className="mb-0">
                <CardBody>
                  {/* Nav for Primary (1) vs Sent (3) */}
                  <Nav tabs className="nav-tabs-custom nav-justified">
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: customActiveTab === "1",
                        })}
                        onClick={() => toggleCustom("1")}
                      >
                        <i className="mdi mdi-inbox me-2 align-middle font-size-18"></i>
                        <span className="d-none d-md-inline-block">Inbox</span>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: customActiveTab === "3",
                        })}
                        onClick={() => toggleCustom("3")}
                      >
                        <i className="mdi mdi-email-newsletter me-2 align-middle font-size-18"></i>
                        <span className="d-none d-md-inline-block">Sent</span>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent
                    activeTab={customActiveTab}
                    className="p-3 text-muted"
                  >
                    {/* Primary Tab */}
                    <TabPane tabId="1">
                      <Row>
                        <Col sm="12">
                          {messages.length > 0 ? (
                            <ul className="message-list mb-0">
                              {messages.map((message, index) => (
                                <li key={message.id}>
                                  <div className="col-mail col-mail-1">
                                    <div className="checkbox-wrapper-mail">
                                      <input
                                        type="checkbox"
                                        id={`chk${index}`}
                                        checked={selectedIds.includes(
                                          message.id,
                                        )}
                                        onChange={() =>
                                          handleCheckboxChange(message.id)
                                        }
                                      />
                                      <label
                                        htmlFor={`chk${index}`}
                                        className="toggle"
                                      ></label>
                                    </div>
                                    <Link
                                      to="#"
                                      className="title"
                                      onClick={() => handleViewMessage(message)}
                                    >
                                      Subject: {message.title}
                                    </Link>
                                  </div>
                                  <div className="col-mail col-mail-2">
                                    <Link
                                      to="#"
                                      className="subject"
                                      onClick={() => handleViewMessage(message)}
                                    >
                                      {message.view === 0 ? (
                                        <span className="bg-warning badge me-2">
                                          Unviewed
                                        </span>
                                      ) : (
                                        <span className="bg-secondary badge me-2">
                                          Viewed
                                        </span>
                                      )}
                                      {message.content.slice(0, 50)}...
                                    </Link>
                                    <div className="date" style={dateStyle}>
                                      <Link
                                        to={`/playerprofile?user=${message.userfrom}`}
                                        style={{
                                          textDecoration: "none",
                                          color: "white",
                                        }}
                                      >
                                        {message.userfrom}
                                      </Link>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p>No Messages</p>
                          )}
                        </Col>
                      </Row>
                    </TabPane>

                    {/* Sent Tab */}
                    <TabPane tabId="3">
                      <React.Fragment>
                        <style>
                          {`
      .subject {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `}
                        </style>

                        {/* Your message rendering code */}
                        <Row>
                          <Col sm="12">
                            {messages.length > 0 ? (
                              <ul className="message-list mb-0">
                                {messages.map((message) => (
                                  <li key={message.id}>
                                    <div className="col-mail col-mail-1">
                                      <Link
                                        to="#"
                                        className="title"
                                        onClick={() =>
                                          handleViewMessage(message)
                                        }
                                      >
                                        {message.title}
                                      </Link>
                                    </div>
                                    <div className="col-mail col-mail-2 d-flex justify-content-between align-items-center">
                                      <div className="date text-nowrap text-muted me-3">
                                        {message.date}
                                      </div>
                                      <Link
                                        to="#"
                                        className="subject text-truncate"
                                        style={{ flex: 1 }}
                                        onClick={() =>
                                          handleViewMessage(message)
                                        }
                                      >
                                        <span className="bg-primary badge me-2">
                                          Sent
                                        </span>
                                        {message.content.slice(0, 50)}...
                                      </Link>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p>No Messages</p>
                            )}
                          </Col>
                        </Row>
                      </React.Fragment>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for viewing a single message */}
        <Modal isOpen={modalOpen} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>
            {selectedMessage?.title ?? "Message"}
          </ModalHeader>
          <ModalBody style={modalBodyStyle}>
            {selectedMessage && (
              <>
                <p>
                  <strong>{customActiveTab === "3" ? "To:" : "From:"} </strong>
                  {customActiveTab === "3"
                    ? selectedMessage.to
                    : selectedMessage.from}
                </p>
                <p>{selectedMessage.content}</p>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {selectedMessage &&
              customActiveTab === "1" &&
              (selectedMessage.report === 1 ? (
                <Button color="danger" disabled>
                  Reported
                </Button>
              ) : (
                <Button
                  color="danger"
                  onClick={() => handleReportMessage(selectedMessage.id)}
                >
                  Report
                </Button>
              ))}

            {selectedMessage && (
              <Button
                color="primary"
                onClick={() =>
                  handleReply(
                    customActiveTab === "3"
                      ? selectedMessage.to
                      : selectedMessage.from,
                  )
                }
              >
                Reply
              </Button>
            )}

            <Button color="secondary" onClick={toggleModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Delete All Confirmation Modal */}
        <Modal isOpen={deleteAllModal} toggle={closeDeleteAllModal} centered>
          <ModalHeader toggle={closeDeleteAllModal} className="game-card-header">
            <h4 className="game-card-title">
              <i className="mdi mdi-alert-circle me-2"></i>
              Confirm Delete All Messages
            </h4>
          </ModalHeader>
          <ModalBody className="game-card-body">
            <div className="game-alert game-alert-warning">
              <div className="game-alert-icon">
                <i className="mdi mdi-alert"></i>
              </div>
              <div className="game-alert-text">
                Are you sure you want to delete all messages? This action cannot be undone.
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="game-card-body border-top border-light">
            <button className="game-button game-button-sm" onClick={closeDeleteAllModal}>
              Cancel
            </button>
            <button className="game-button game-button-danger game-button-sm anim-pulse" onClick={handleDeleteAll}>
              <span>Delete All</span>
              <i className="mdi mdi-delete me-2"></i>
            </button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Inbox;
